import React from "react";
import { useTranslation } from "react-i18next";
import { LockOpenIcon } from "@heroicons/react/outline";
import { usePeriodLock } from "../../../../Context";
import { formatPeriodDate, getSupersedeInfo } from "../../../../Utils";

/**
 * ReportRevisionNotice — tells the reader this report covers a period that was
 * reopened after it was closed, so a copy printed earlier may no longer match.
 *
 * Metadata, not an alarm: neutral colours, no animation, not dismissible. The
 * numbers on screen are correct — it is the *paper* that may be stale, and the
 * app has no way of knowing whether the user ever printed any. So the wording
 * says a copy "may" no longer match, never that their file has expired.
 *
 * It reads the period itself rather than taking `info` as a prop, the same way
 * PeriodLockBanner does. Duplicate GETs collapse inside requestPeriodStatus, so
 * thirteen report pages mounting this cost one request per business. A book that
 * was never reopened renders nothing at all.
 *
 * @param businessID  business the report belongs to — v2 pages pass
 *                    user.mainBusiness?.id, v1 selectedBusiness?.id, embeds
 *                    searchParams.get("id"). Falsy disables the hook entirely.
 * @param startDate   omit for as-of reports (Balance Sheet, Capital Statement):
 *                    their range starts at the beginning of the book.
 * @param endDate     date the report is drawn up to.
 * @param className   border/rounding for the surrounding layout; the v2 pages sit
 *                    under a header and want a bottom border, v1 and the embeds
 *                    float in a stack and want a full box.
 */
const ReportRevisionNotice = ({
  businessID,
  startDate,
  endDate,
  className = "",
}) => {
  const { t, i18n } = useTranslation();
  const { history } = usePeriodLock(businessID);

  const info = getSupersedeInfo(history, startDate, endDate);
  if (!info) return null;

  return (
    <div
      className={`flex items-start gap-2 px-4 py-2 sm:px-6 text-sm text-gray-600 bg-gray-50 ${className}`}
    >
      <LockOpenIcon
        className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400"
        strokeWidth={1.5}
        aria-hidden="true"
      />
      <span>
        {t("accounting.period.reopenedNotice", {
          count: info.count,
          date: formatPeriodDate(info.lastAt, i18n.language),
        })}
      </span>
    </div>
  );
};

export default ReportRevisionNotice;
